
import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { addDays } from 'date-fns';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';


import { globalConfig } from 'config';

import { IDirectMessageInfo, IReportProcessing, ReportProcessingType, _RecipientType, defaultReportProcessing, } from './models/ReportProcessing';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom, currentUserSessionAtom, isSaveLoadingAtom } from 'library/store';
import useReportProcessingService, { useBasicFilterSmsReportProcessing, useBasicFilterReportProcessing } from './services/ReportProcessing';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Avatar, Checkbox, Chip, FormControlLabel, IconButton, InputAdornment, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import NumberFormat from 'react-number-format';

import useEnumerationService from 'features/configuration/services/Enumeration';

import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import { useBasicFilterEnumeration } from 'features/configuration/services/Enumeration';
import IEnumeration, {
  IEnumerationItem, Enum_STUDY_LEVEL, Enum_STUDY_SECTION, Enum_STUDY_LANGUAGE,
  Enum_REGISTRATION_STATUS, Enum_PERSON_GENDER, Enum_STUDY_TYPE,
  Enum_STUDY_CYCLE
} from 'features/configuration/models/Enumeration';
import { isFalsy } from 'utility-types';
import { typographyGroupBoxStyling } from 'themes/commonStyles';
import { ISchoolYearClass, ISchoolYearClassTemplate, ISchoolYearPeriod, ISchoolYearPeriodExam } from 'features/setup/models/SchoolYear';

import useSchoolYearService, { useBasicFilterSchoolYear } from 'features/setup/services/SchoolYear';
import useBillingService from 'features/finance/services/Billing';
import useReportService from 'features/configuration/services/Report';


import useUtils from 'library/utils';


import { debounce } from 'lodash';
import { IReport } from 'features/configuration/models/Report';
import { useBasicFilterReport } from 'features/configuration/services/Report';
import { RichWysiwygEditor } from 'components/ui/RichWysiwygEditor';
import { stripHtml } from 'string-strip-html';
import { ReportCardType } from 'features/production/models/RegistrationMark';

export const InstantMessageForm: FC<IReportProcessing> = (props: IReportProcessing = {
  ...defaultReportProcessing,
  validityStartDate: new Date(), validityEndDate: addDays(new Date(), 1)
}) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { id } = useParams();

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { retrieveEntity, retrieveData, openEntityActionDrawer, checkEntitySaveAuthorization } = entityService();

  const { createReportProcessing, updateReportProcessing } = useReportProcessingService();

  const { getEnumerationItemsByEnumerationCodes, getAsOptions } = useEnumerationService();
  const { getRecipientsCount } = useBillingService();

  const { getBasicReportDocumentsByReport } = useReportService();

  const schoolYearServiceRef = useRef(useSchoolYearService());

  const { readExcel, extractVariables, mergeTemplate } = useUtils();

  const [currentUserSession, setCurrentUserSession] = useRecoilState(currentUserSessionAtom);
  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const { language: lg, schoolYears, applicationSetup } = useRecoilValue(currentUserSessionAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterReportProcessing = useBasicFilterSmsReportProcessing(
    (event: React.MouseEvent<unknown>, row: IReportProcessing) => {

      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration(
    (event: React.MouseEvent<unknown>, row: IEnumeration) => {
      const { id, name, description } = row;

      // setValue('enumerationId', id);
      // setValue('enumerationName', name);

      setOpenEnumerationFilter(false);
    }
  );


  const [openReportFilter, setOpenReportFilter] = useState(false);
  const basicFilterReport = useBasicFilterReport(
    async (event: React.MouseEvent<unknown>, row: IReport) => {
      if (isFalsy(row)) return;
      const { id, name, entityName, description } = row;

      setValue('reportId', id);
      setValue('reportName', name);

      const reportDocuments = await getBasicReportDocumentsByReport(id);

      const docFR = reportDocuments.find(d => d.documentLanguage.includes('fr'));
      const docEN = reportDocuments.find(d => d.documentLanguage.includes('en'));

      setValue('messageBodyFR', docFR?.shortMessageBody ?? '');
      setValue('messageBodyEN', docEN?.shortMessageBody ?? '');

      setOpenReportFilter(false);
    }
  );


  const methods = useForm<IReportProcessing>({
    defaultValues: {
      ...defaultReportProcessing,
      validityStartDate: new Date(), validityEndDate: addDays(new Date(), 1),
      schoolYearId: applicationSetup.currentSchoolYearId,
      recipientFilterOrigin: 'registration',
      currentRecipientLanguage: lg.includes('fr') ? 'fr-FR' : 'en-US'
    }
  });
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  const watchType = watch('type');
  const watchRecipientNumber = watch('reportRecipient');

  const watchMessageBody = watch('messageBody');
  const watchMessageBodyFR = watch('messageBodyFR');
  const watchMessageBodyEN = watch('messageBodyEN');

  const watchReportFileNameFR = watch('reportFileNameFR');
  const watchReportFileNameEN = watch('reportFileNameEN');


  const watchRecipientType = watch('recipientType');
  const watchRecipientFilterOrigin = watch('recipientFilterOrigin');

  const watchCountRecipient = watch('countRecipient');
  const watchCountDistinctRecipient = watch('countDistinctRecipient');

  const watchCountRegistration = watch('countRegistration');

  const watchSchoolYearId = watch('schoolYearId');

  const watchSchoolYearPeriodId = watch('schoolYearPeriodId');
  const watchSchoolYearPeriodExamId = watch('schoolYearPeriodExamId');

  const watchStudySection = watch('studySection');
  const watchStudyLanguage = watch('studyLanguage');
  const watchStudyLevel = watch('studyLevel');

  const watchSchoolYearClassTemplateId = watch('schoolYearClassTemplateId');
  const watchStudyType = watch('studyType');

  const watchSchoolYearClassId = watch('schoolYearClassId');
  const watchGender = watch('gender');
  const watchOldStudentStatus = watch('oldStudentStatus');
  const watchRepeaterStatus = watch('repeaterStatus');
  const watchSocialCaseStatus = watch('socialCaseStatus');
  const watchAmountToPayType = watch('amountToPayType');
  const watchDueDate = watch('dueDate');
  const watchRecipientSource = watch('recipientSource');
  const watchStudyCycle = watch('studyCycle');

  const watchHasDeparted = watch('hasDeparted');

  const watchIsTemplateMessage = watch('isTemplateMessage');

  const watchDirectMessageInfos = watch("directMessageInfos") ?? [];

  const watchReportCardType = watch('reportCardType');


  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<IReportProcessing>, Error, IReportProcessing>(
    _id > 0 ? updateReportProcessing : createReportProcessing, {
    onSuccess: (data: IResult<IReportProcessing>) => {
      enqueueSnackbar('_Operation_done', {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      _setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);

      queryClient.invalidateQueries(['ReportProcessing', data.data.id]);
      const btn = document.getElementById(`btnNew`);
      btn?.click();
    },
    onError: (err: Error) => {

      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });

  const { data: _data, refetch } = useQuery<IReportProcessing>(['ReportProcessing', _id], () => retrieveEntity('ReportProcessing', _id),
    { refetchOnWindowFocus: false, enabled: false });


  const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'Registration'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_STUDY_LEVEL, Enum_STUDY_SECTION, Enum_STUDY_LANGUAGE, Enum_STUDY_CYCLE, Enum_REGISTRATION_STATUS, Enum_PERSON_GENDER,
      Enum_STUDY_TYPE]));


  const [anchorElContract, setAnchorElContract] = useState<null | HTMLElement>(null);
  const handleContractSwitch = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElContract(event.currentTarget);
  };


  const [currentContractId, setCurrentContractId] = useState<number>(0);
  const [currentContractDescription, setCurrentContractDescription] = useState<string>('');

  const [currentFileName, setCurrentFileName] = useState<string>('');

  const [currentFileNameFR, setCurrentFileNameFR] = useState<string>('');
  const [currentFileNameEN, setCurrentFileNameEN] = useState<string>('');

  const [currentBase64File, setCurrentBase64File] = useState<string>('');

  const [schoolYearClassTemplates, setSchoolYearClassTemplates] = useState<ISchoolYearClassTemplate[]>([]);

  const [schoolYearPeriods, setSchoolYearPeriods] = useState<ISchoolYearPeriod[]>([]);
  const [schoolYearPeriodExams, setSchoolYearPeriodExams] = useState<ISchoolYearPeriodExam[]>([]);

  const [classes, setClasses] = useState<ISchoolYearClass[]>([]);

  const [recipients, setRecipients] = useState<string[]>([]);

  const handleChangeType = (event: React.MouseEvent<HTMLElement>, _newType: ReportProcessingType) => {
    if (_newType === null) return;
    setValue('type', _newType);

    setValue('recipientSource', _newType === 'email' ? 'email' : 'portable');
  };

  const handleChangeRecipientType = (event: React.MouseEvent<HTMLElement>, _newRecipientType: _RecipientType) => {
    if (_newRecipientType === null) return;
    setValue('recipientType', _newRecipientType);
  };

  const handleChangeReportCardType = (event: React.MouseEvent<HTMLElement>, _newReportCardType: ReportCardType) => {
    if (_newReportCardType === null) return;
    setValue('reportCardType', _newReportCardType);
  };


  const handleClickSearchReport = (event: any) => {

    setOpenReportFilter(true);
  }

  type FilePurpose = 'recipient' | 'file-fr' | 'file-en';

  function openFileDialog(filePurpose: FilePurpose) {
    const fileUploadId = filePurpose === 'recipient' ? 'file-upload-recipient' :
      filePurpose === 'file-fr' ? 'file-upload-fr' : 'file-upload-en';

    const input = document.getElementById(fileUploadId) as HTMLInputElement | null;
    if (input) {
      input.value = ''; // ✅ reset so even same file triggers onChange
      input.click();
    }

    //(document as any).getElementById("file-upload").click();
  }

  const setFile = async (_event: any, filePurpose: FilePurpose) => {

    if (_event.target.files?.length) {

      const variables = extractVariables(`${getValues().messageBodyFR} - ${getValues().messageBodyEN}`);

      if (variables.length === 0) {
        enqueueSnackbar(`${t('This is not a validate template message, it must have variable @${var}')}.`, {
          variant: 'warning',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });
        return;
      }

      if (filePurpose === 'recipient') {
        let f = _event.target.files![0];
        const rows = await readExcel(_event.target.files[0]);
        const availableColumns = Object.keys(rows[0]);

        // check mandatory groups
        const mandatoryGroups = [
          ["_recipient", "_destinataire"],
          ["_language", "_langue"]
        ];

        const missing: string[] = [];
        mandatoryGroups.forEach(group => {
          if (!group.some(col => availableColumns.includes(col))) {
            missing.push(group.join(" or "));
          }
        });
        variables.forEach(v => {
          if (!availableColumns.includes(v)) missing.push(v);
        });

        if (missing.length > 0) {
          enqueueSnackbar(`${t('Missing columns')}: ${missing.join(', ')}`, {
            variant: 'warning',
            anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
          });

          return;
        }

        // ✅ Transform rows into directMessageInfos
        const infos = rows.map(r => {
          const recipient = (r["_recipient"] ?? r["_destinataire"])?.toString() ?? '';
          const language = (r["_language"] ?? r["_langue"])?.toString() ?? '';

          // put all variables into object
          const variablesObj: Record<string, string> = {};
          variables.forEach(v => {
            variablesObj[v] = r[v] !== undefined && r[v] !== null ? String(r[v]) : "";
          });

          return {
            recipient,
            language,
            variablesObj
          } as IDirectMessageInfo;
        });

        setValue('directMessageInfos', infos);
        setCurrentFileName(f.name);
      }

      // preview only 10
      //const previewMsgs = rows.slice(0, 10).map(r => mergeTemplate(template, r));
      //setPreview(previewMsgs);
    }


  }

  const acceptedFile = `
    image/*,
    video/*,
    application/pdf,
    application/msword,
    application/vnd.openxmlformats-officedocument.wordprocessingml.document,
    application/vnd.ms-excel,
    application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
    application/vnd.ms-powerpoint,
    application/vnd.openxmlformats-officedocument.presentationml.presentation,
    text/plain,
    text/csv
  `
  const setAttachedFile = (_event: any, filePurpose: FilePurpose) => {
    let f = _event.target.files![0];
    var reader = new FileReader();

    reader.onload = function () {

      var binaryString = reader.result as string;

      const base64String = binaryString
        .replace('data:', '')
        .replace(/^.+,/, '');

      setValue(filePurpose === 'file-fr' ? 'base64ReportFR' : 'base64ReportEN', base64String);
      setValue(filePurpose === 'file-fr' ? 'reportFileNameFR' : 'reportFileNameEN', f.name);

    };

    reader.onerror = function () {
      console.log("File load failed");
    };

    reader.readAsDataURL(f);
  };


  useEffect(() => {
    async function _() {
      const arr = await schoolYearServiceRef.current.getSchoolYearClassTemplates(watchSchoolYearId, false, '', '');

      setSchoolYearClassTemplates(arr);
      //setSchoolYearClassTemplates( arr.filter( x => x.studyLanguage === watchStudyLanguage) );
    }

    _();
  }, [watchSchoolYearId]);

  useEffect(() => {
    async function _() {

      if (watchSchoolYearId <= 0) return;

      const schoolYear = schoolYears.find(x => x.id === watchSchoolYearId);
      if (isFalsy(schoolYear)) return;

      //setValue('schoolYearStartDate', schoolYear.startDate);
      //setValue('schoolYearEndDate', schoolYear.endDate);

      const periods = await schoolYearServiceRef.current.getSchoolYearPeriods(watchSchoolYearId);
      setSchoolYearPeriods([...periods]);

      const exams = await schoolYearServiceRef.current.getSchoolYearPeriodExams(watchSchoolYearId);
      setSchoolYearPeriodExams([...exams]);
    }

    _();

  }, [watchSchoolYearId]);


  useEffect(() => {
    async function _() {

      const studyTypes = (enumItems || []).filter(e => e.enumerationCode === Enum_STUDY_TYPE);
      if (studyTypes.length === 1)
        setValue('studyType', studyTypes[0].code);

      if (watchSchoolYearId > 0 /*&& watchSchoolYearClassTemplateId > 0*/) {
        const arr = await schoolYearServiceRef.current.getSchoolYearClasses({
          schoolYearId: watchSchoolYearId,
          name: '',
          studyType: '', //watchStudyType, 
          studySection: '',
          studyLanguage: '', //watchStudyLanguage,
          studyLevel: ''
        });

        setClasses(arr);
      }
    }

    _();
  }, [enumItems, setValue, watchSchoolYearId]);


  useEffect(() => {
    setCurrentFormNameAtom(t('Instant SMS'));
    setCurrentBasicTextFilterProps(basicFilterReportProcessing);
  }, []);

  useEffect(() => {
    setRecipients([...watchRecipientNumber.split(',')].filter(n => n.trim() !== ''));
  }, [watchRecipientNumber]);

  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
    and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        

    if (_id > 0)
      retrieveData('ReportProcessing', _id, refetch);
  }, [_id]);



  const debouncedFilterChange = useRef(
    debounce(async (pSchoolYearId: number, pSchoolYearClassTemplateId: number, pSchoolYearClassId: number,
      pStudySection: string, pStudyLanguage: string, pStudyCycle: string, pStudyLevel: string, pStudyType: string, pGender: string,
      pOldStudentStatus: string, pRepeaterStatus: string, pSocialCaseStatus: string, pHasDeparted: boolean,
      pAmountToPayType: string, pDueDate: Date, pPecipientSource: string) => {

      const { countRecipient, countDistinctRecipient, countRegistration } =
        await getRecipientsCount(pSchoolYearId, pSchoolYearClassTemplateId, pSchoolYearClassId,
          pStudySection, pStudyLanguage, pStudyCycle, pStudyLevel, pStudyType, pGender,
          pOldStudentStatus, pRepeaterStatus, pSocialCaseStatus, pHasDeparted, pAmountToPayType, pDueDate, pPecipientSource,
          false, false);

      setValue('countRegistration', countRegistration);
      setValue('countDistinctRecipient', countDistinctRecipient);
      setValue('countRecipient', countRecipient);

    }, 1000)
  ).current;

  useEffect(() => {
    async function _() {
      if (watchRecipientType === 'direct') return;

      debouncedFilterChange(watchSchoolYearId, watchSchoolYearClassTemplateId, watchSchoolYearClassId,
        watchStudySection, watchStudyLanguage, watchStudyCycle, watchStudyLevel, watchStudyType, watchGender,
        watchOldStudentStatus, watchRepeaterStatus, watchSocialCaseStatus, watchHasDeparted, watchAmountToPayType, watchDueDate,
        watchRecipientSource);
    }
    _();
  }, [watchRecipientType,
    watchSchoolYearId, watchSchoolYearClassTemplateId, watchSchoolYearClassId, watchGender,
    watchOldStudentStatus, watchRepeaterStatus, watchSocialCaseStatus, watchHasDeparted,
    watchAmountToPayType, watchDueDate, watchRecipientSource,
    watchStudyCycle, watchStudyLevel, watchStudyType, watchStudySection, watchStudyLanguage, debouncedFilterChange]);


  useEffect(() => {

    if (_data && _data.id > 0) {
      reset({ ..._data, recipientType: 'direct' });
    }
  }, [_data]);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);
    reset({ ...defaultReportProcessing, validityStartDate: new Date(), validityEndDate: addDays(new Date(), 1) });
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {

    if (!checkEntitySaveAuthorization('InstantMessage', _id)) {
      setIsSaveLoading(false);
      return;
    }

    const data = getValues();

    //const {id} = currentUserSession;
    if (data.recipientType === 'direct' && !data.isTemplateMessage && data.reportRecipientsWithLanguage.length === 0) {
      enqueueSnackbar(t('There is no recipient'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.recipientType === 'direct' && data.isTemplateMessage && data.directMessageInfos.length === 0) {
      enqueueSnackbar(t('There is no recipient'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.type === 'sms' && data.messageBodyFR.trim() === '' && data.messageBodyEN.trim() === '') {
      enqueueSnackbar(t('Message body is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if(data.recipientType === 'filter' && data.recipientFilterOrigin === 'reportCard' 
        && ( (data.reportCardType === 'period' && data.schoolYearPeriodId <= 0) || 
             (data.reportCardType === 'exam' && data.schoolYearPeriodExamId <= 0) ) ){
      enqueueSnackbar(t('You have to select period or/and exam'), { variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }



    mutate({ ...data });
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('ReportProcessing', _id);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={3} columnSpacing={3}>
          <Grid item xs={12} md={7} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 1, width: '100%' }} >
                <ToggleButtonGroup value={watchType} exclusive key={watchType} size="small"
                  onChange={handleChangeType} aria-label="text alignment" fullWidth>
                  <ToggleButton value="sms" aria-label="centered">
                    {t('Sms')}
                  </ToggleButton>
                  <ToggleButton value="whatsApp" aria-label="centered">
                    {t('WhatsApp')}
                  </ToggleButton>
                  <ToggleButton value="email" aria-label="centered">
                    {t('Email')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <FormControlLabel sx={{ width: 'calc(25% - 8px)' }}
                  label={t('Template message ?')}
                  control={
                    <Controller
                      name='isTemplateMessage'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
                {watchIsTemplateMessage && <Controller
                  key={`reportName}`}
                  render={({ field }) => <TextField label={t('Report')} {...field}
                    inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                    sx={{ width: `calc(70% - 8px)` }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton color="primary" onClick={handleClickSearchReport}>
                            <ArrowDropDownCircleIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }} />}
                  name={`reportName`}
                  control={control}
                />}
              </Box>
              <Box sx={{ mt: 3, width: '100%' }} >
                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Controller control={control}
                  name='validityStartDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DateTimePicker label={t('Effective date')}

                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                    />)}
                />
                <Controller control={control}
                  name='validityEndDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DateTimePicker label={t('Expiration date')}

                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                    />)}
                />
                {/* <TextField sx={{ width: 'calc(50% - 8px)' }} id="reportRecipient" label={t('Recipient')}
                  {...register('reportRecipient')}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' }, readOnly: true }}
                /> */}
              </Box>
              {openReportFilter && <FormDialog open={openReportFilter} maxWidth='md'
                okText={t('OK')} cancelText='' title={t('Report filter')} onCancel={() => { }}
                onClose={() => { setOpenReportFilter(false); }} onOk={() => { setOpenReportFilter(false); }}  >
                <BasicTextFilterForm<IReport> {...basicFilterReport} />
              </FormDialog>}


              {/* <Box sx={{ mt: 1, width: '100%' }} >
                <FormControlLabel sx={{ width: 'calc(25% - 8px)' }}
                  label={t('Is Processed ?')}
                  control={
                    <Controller
                      name='isProcessed'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} disabled />}
                    />} />
                <Controller control={control}
                  name='processingDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DateTimePicker label={t('Processing date')}

                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                    />)}
                />
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="lastErrorMessage" label={t('Last error message')}
                  {...register('lastProcessingError')}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' }, readOnly: true }}
                />
              </Box> */}


              {watchType === 'sms' && <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(100% - 8px)' }} id="messageBodyFR" label={`${t('MessageBody')} FR`} multiline rows={3}
                  {...register('messageBodyFR')}
                  helperText={`${watchMessageBodyFR.length} ${t('character')}(s) :: ${watchIsTemplateMessage ? t('for template messages variables are like @{var}') : ''}`}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                />
              </Box>}
              {watchType === 'sms' && <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(100% - 8px)' }} id="messageBodyEN" label={`${t('MessageBody')} EN`} multiline rows={3}
                  {...register('messageBodyEN')}
                  helperText={`${watchMessageBodyEN.length} ${t('character')}(s) :: ${watchIsTemplateMessage ? t('for template messages variables are like @{var}') : ''} `}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                />
              </Box>}

              {watchType !== 'sms' && (
                <Box
                  sx={{
                    mt: 1,
                    width: '100%',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'column', md: 'row', },
                    justifyContent: 'space-between',
                    gap: 2, // space between editors
                  }}
                  id="messageBody--"
                >
                  {/* French Message Editor */}
                  <Box sx={{ width: { xs: '100%', md: '50%' } }} id="messageBodyFR">
                    <Typography variant="subtitle1" gutterBottom>
                      🇫🇷 {t('Message in french')}
                    </Typography>
                    {watchType === 'email' && <Controller
                      name="messageObjectFR"
                      control={control}
                      rules={{ required: "Email is required" }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t('Email object')}
                          fullWidth
                          margin="dense"
                          inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                        />
                      )}
                    />}
                    {/* File Upload FR */}
                    <Tooltip
                      title={
                        <>
                          <div>{t('The file loaded will be attached to this message.')}</div>
                        </>
                      } >
                      <Button
                        onClick={() => openFileDialog('file-fr')} key={`btn - load file `} id={`btnLoadFileFR`}>
                        <input type="file" id={`file-upload-fr`} accept={acceptedFile} style={{ display: "none" }}
                          onChange={(_event) => setAttachedFile(_event, 'file-fr')} />
                        {isFalsy(watchReportFileNameFR) ? `${t('Click to toad file')} ...` : `${watchReportFileNameFR} -> ${t('Click to change')}`}
                      </Button>
                    </Tooltip>
                    <Controller
                      render={({ field }) => (
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',      // ✅ uses theme color
                            borderRadius: 2,             // ✅ rounded corners
                            p: 1,                        // ✅ internal padding
                            mt: 1,                       // ✅ spacing above
                            minHeight: 150,              // ✅ optional minimum height
                          }}
                        >
                          <RichWysiwygEditor
                            {...field}
                            id="messageBodyFR_email_whatsApp"
                          />
                        </Box>
                      )}
                      name="messageBodyFR"
                      control={control}
                      defaultValue=""
                      rules={{
                        validate: {
                          required: (v) =>
                            (v && stripHtml(v).result.length > 0) || "Description is required",
                          maxLength: (v) =>
                            (v && stripHtml(v).result.length <= 2000) ||
                            "Maximum character limit is 2000",
                        },
                      }}
                    />
                  </Box>

                  {/* English Message Editor */}
                  <Box sx={{ width: { xs: '100%', md: '50%' } }} id="messageBodyEN">
                    <Typography variant="subtitle1" gutterBottom>
                      EN {t('Message in english')}
                    </Typography>
                    {watchType === 'email' && <Controller
                      name="messageObjectEN"
                      control={control}
                      rules={{ required: "Subject is required" }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t('Email object')}
                          fullWidth
                          margin="dense"
                          inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                        />
                      )}
                    />}
                    {/* File Upload FR */}
                    <Tooltip
                      title={
                        <>
                          <div>{t('The file loaded will be attached to this message.')}</div>
                        </>
                      } >
                      <Button
                        onClick={() => openFileDialog('file-en')} key={`btn - load file `} id={`btnLoadFileEN`}>
                        <input type="file" id={`file-upload-en`} accept={acceptedFile} style={{ display: "none" }}
                          onChange={(_event) => setAttachedFile(_event, 'file-en')} />
                        {isFalsy(watchReportFileNameEN) ? `${t('Click to toad file')} ...` : `${watchReportFileNameEN} -> ${t('Click to change')}`}
                      </Button>
                    </Tooltip>
                    <Controller
                      render={({ field }) => (
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',      // ✅ uses theme color
                            borderRadius: 2,             // ✅ rounded corners
                            p: 1,                        // ✅ internal padding
                            mt: 1,                       // ✅ spacing above
                            minHeight: 150,              // ✅ optional minimum height
                          }}
                        >
                          <RichWysiwygEditor
                            {...field}
                            id="messageBodyEN_email_whatsApp"
                          />
                        </Box>
                      )}
                      name="messageBodyEN"
                      control={control}
                      defaultValue=""
                      rules={{
                        validate: {
                          required: (v) =>
                            (v && stripHtml(v).result.length > 0) || "Description is required",
                          maxLength: (v) =>
                            (v && stripHtml(v).result.length <= 2000) ||
                            "Maximum character limit is 2000",
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}


            </Stack>
          </Grid>
          <Grid item xs={12} md={5} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 1, width: '100%' }} >
                <ToggleButtonGroup value={watchRecipientType} exclusive key={watchRecipientType} size="small"
                  onChange={handleChangeRecipientType} aria-label="text alignment" fullWidth>
                  <ToggleButton value="direct" aria-label="centered">
                    {t('Recipient')}
                  </ToggleButton>
                  <ToggleButton value="filter" aria-label="centered">
                    {t('Filter')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {(watchRecipientType === 'direct') && <>
                <Box sx={{ mt: 1, width: '100%', display: 'flex', gap: 2 }} >
                  {!watchIsTemplateMessage && <TextField sx={{ width: 'calc(50% - 8px)' }} id="messageBody"
                    label={t('Add recipient by pressing ENTER')}
                    {...register('currentRecipient')} multiline maxRows={1}
                    InputProps={{
                      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          const { currentRecipient, reportRecipientsWithLanguage } = getValues();

                          const newLines = currentRecipient
                            .split('\n').map((line) => line.trim())
                            .filter((line) => line !== '');

                          const parsed = newLines
                            .map((line) => {
                              // Try to split by common separators
                              const parts = line.split(/[,:\s]+/).map((p) => p.trim()).filter(Boolean);

                              if (parts.length === 0) return null;

                              const number = parts[0];
                              const language = parts[1] || getValues().currentRecipientLanguage;

                              // If no language and no default, skip
                              if (!language) {
                                enqueueSnackbar(t('You must specfied the language'), {
                                  variant: 'warning',
                                  anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
                                });
                                setIsSaveLoading(false);
                                return;
                              }

                              // Check language
                              if (!language.includes('en') && !language.includes('fr')) return null;

                              return { recipient: number, language: language.includes('fr') ? 'fr-FR' : 'en-US' };
                            })
                            .filter((item): item is { recipient: string; language: string } => item !== null);

                          if (parsed.length > 0) {
                            // Deduplicate by recipient+language pair
                            const merged = [
                              ...reportRecipientsWithLanguage,
                              ...parsed
                            ].reduce((acc, cur) => {
                              const key = `${cur.recipient}-${cur.language}`;
                              if (!acc.some((x) => `${x.recipient}-${x.language}` === key)) {
                                acc.push(cur);
                              }
                              return acc;
                            }, [] as { recipient: string; language: string }[]);

                            setValue('reportRecipientsWithLanguage', merged, { shouldValidate: true });

                            // Optionally set main "reportRecipient" (just first number for compatibility)
                            if (merged.length > 0) {
                              setValue('reportRecipient', merged[0].recipient);
                            }

                          }

                          setValue('currentRecipient', '');
                        }
                      },
                    }}
                    inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  />}
                  {watchIsTemplateMessage && <Tooltip
                    title={
                      <>
                        <div>{t('The file loaded is an Excel file with:')}</div>
                        <div>- {t('Recipient (_recipient or _destinataire)')}</div>
                        <div>- {t('Language (_language or _langue)')}</div>
                        <div>- {t('Other columns used in template message as @${var}')}</div>
                      </>
                    } >
                    <Button
                      onClick={() => openFileDialog('recipient')} key={`btn - load file `} id={`btnLoadFile`}>
                      <input type="file" id={`file-upload-recipient`} accept={'.xlsx'} style={{ display: "none" }}
                        onChange={(_event) => setFile(_event, 'recipient')} />
                      {isFalsy(currentFileName) ? `${t('Click to toad file')} ...` : `${currentFileName} -> ${t('Click to change')}`}
                    </Button>
                  </Tooltip>
                  }
                  <Box sx={{ width: 'calc(10% - 8px)' }} />
                  <Controller name='currentRecipientLanguage' control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextField select onChange={onChange} value={value}
                        sx={{ width: 'calc(25% - 8px)' }}
                        id="currentRecipientLanguage"
                        label={t('Document language')} >
                        <MenuItem value="fr-FR">{t('French')}</MenuItem>
                        <MenuItem value="en-US">{t('English')}</MenuItem>
                      </TextField>
                    )}
                  />
                </Box>
                <Box sx={{ mt: 1, width: '100%' }} key={getValues().reportRecipientsWithLanguage.map(r => `${r.recipient}-${r.language}`).join('-')}>
                  {
                    getValues().reportRecipientsWithLanguage.map((item, idx) =>
                      !isFalsy(item) && <Chip sx={{ ml: 1, mr: 1, mb: 1 }}
                        key={`recipient-${item.recipient}-${item.language}-${idx}`}
                        label={`${item.recipient} (${item.language})`}
                        color="primary" variant="outlined"
                        onDelete={() => {
                          const filtered = getValues().reportRecipientsWithLanguage.filter(
                            (r) => !(r.recipient === item.recipient && r.language === item.language)
                          );
                          setValue('reportRecipientsWithLanguage', filtered, { shouldValidate: true });
                        }}
                      />)
                  }
                </Box>
                <Box sx={{ mt: 1, width: '100%' }} key={`${watchDirectMessageInfos.length} ${watchDirectMessageInfos.map(x => x.recipient).join('-')}`}>
                  <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                    {` ${watchIsTemplateMessage ? new Set(watchDirectMessageInfos.map((d: any) => d.recipient) ?? []).size :
                      getValues().reportRecipientsWithLanguage.length} ${t(('Recipient'))}(s), ${watchDirectMessageInfos.length} Message(s)`}
                  </Typography>
                </Box>
              </>}
              {(watchRecipientType === 'filter') && <>
                <Box sx={{ mt: 1, width: '100%' }} >
                  <Controller name='recipientFilterOrigin' control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="recipientFilterOrigin"
                        label={t('Origin')} inputProps={{ readOnly: false }} focused >
                        <MenuItem value='registration'>{`${t('Registration')} / ${t('Student')}`}</MenuItem>
                        <MenuItem value='reportCard'>{`${t('Report card')} ...`}</MenuItem>
                        <MenuItem value='parent'>{`${t('Parent')} / ${t('_Father')} / ${t('_Mother')} ...`}</MenuItem>
                        <MenuItem value='supplier'>{`${t('Supplier')}`}</MenuItem>
                      </TextField>
                    )}
                  />
                </Box>
                {(watchRecipientFilterOrigin === 'reportCard') && <Box sx={{ mt: 1, width: '100%' }} >
                  <ToggleButtonGroup size="small"
                    color="primary"
                    value={watchReportCardType}
                    exclusive
                    onChange={(event, newType) => handleChangeReportCardType(event, newType)}
                    aria-label="Small sizes" fullWidth
                  >
                    <ToggleButton value='exam'>{t('Exam')}</ToggleButton>
                    <ToggleButton value='period'>{t('Period')}</ToggleButton>
                    <ToggleButton value='year'>{t('Year')}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>}
                {(['registration', 'reportCard'].includes(watchRecipientFilterOrigin)) &&
                  <>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='schoolYearId' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                            label={t('School year')} inputProps={{ readOnly: false }} focused >
                            {schoolYears && schoolYears.map(
                              (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                      <Controller name='studyType' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                            label={t('Study type')} inputProps={{ readOnly: false }} focused >
                            <MenuItem value=''>{':::'}</MenuItem>
                            {enumItems && enumItems.filter(e =>
                              e.enumerationCode === Enum_STUDY_TYPE).map(
                                (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                    </Box>
                    {(watchRecipientFilterOrigin === 'reportCard') && <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='schoolYearPeriodId' control={control}

                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                            label={t('Period')} inputProps={{ readOnly: false }} focused >
                            {schoolYearPeriods && schoolYearPeriods.map(
                              (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                      <Controller name='schoolYearPeriodExamId' control={control}

                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                            label={t('Exam')} inputProps={{ readOnly: false }} focused >
                            {schoolYearPeriodExams && schoolYearPeriodExams.filter(x => x.schoolYearPeriodId === watchSchoolYearPeriodId).map(
                              (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                    </Box>}
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='studySection' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studySection"
                            label={t('Study section')} inputProps={{ readOnly: false }} focused >
                            {enumItems && enumItems.filter(e =>
                              e.enumerationCode === Enum_STUDY_SECTION).map(
                                (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                      <Controller name='studyLanguage' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studyLanguage"
                            label={t('Study language')} inputProps={{ readOnly: false }} focused >
                            <MenuItem value=''>{':::'}</MenuItem>
                            {enumItems && enumItems.filter(e =>
                              e.enumerationCode === Enum_STUDY_LANGUAGE).map(
                                (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='studyCycle' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="studyCycle"
                            label={t('Study cycle')} inputProps={{ readOnly: false }} focused >
                            <MenuItem value=''>{':::'}</MenuItem>
                            {enumItems && enumItems.filter(e =>
                              e.enumerationCode === Enum_STUDY_CYCLE).map(
                                (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                      <Controller name='studyLevel' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="studyLevel"
                            label={t('Study level')} inputProps={{ readOnly: false }} focused >
                            <MenuItem value=''>{':::'}</MenuItem>
                            {enumItems && enumItems.filter(e =>
                              e.enumerationCode === Enum_STUDY_LEVEL).map(
                                (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                      <Controller name='schoolYearClassTemplateId' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(30% - 8px)' }} id="schoolYearClassTemplateId"
                            label={`${t('Study level')} - ${t('Class')} `} inputProps={{ readOnly: false }} focused >
                            <MenuItem key={0} value={0}>::::</MenuItem>
                            {schoolYearClassTemplates.filter(x => x.studySection === watchStudySection
                              && x.studyLanguage === watchStudyLanguage && x.studyLevel === watchStudyLevel).map(
                                (x, idx) => <MenuItem key={x.id} value={x.id}>{x.classTemplateName}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='schoolYearClassId' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="schoolYearClassId"
                            label={t('Class')} inputProps={{ readOnly: false }} focused
                          >
                            <MenuItem key={0} value={0}>::::</MenuItem>
                            {classes.filter(x => x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId
                              && x.isActive && x.studyType === watchStudyType && x.studySection === watchStudySection && x.studyLanguage === watchStudyLanguage).map(
                                (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                      <Controller name="gender" control={control}
                        render={({ field }) => (
                          <TextField select label={t('Gender')}
                            sx={{ width: '33%' }} {...field}
                          >
                            <MenuItem key={''} value={''}>{''}</MenuItem>
                            {(enumItems || [])
                              .filter(e => e.enumerationCode === Enum_PERSON_GENDER).map(e => (
                                <MenuItem key={e.code} value={e.code}>{e.name}</MenuItem>
                              ))}
                          </TextField>
                        )}
                      />
                      <FormControlLabel sx={{ ml: 2, width: 'calc(30% - 8px)' }}
                        label={t('With / Expel ?')}
                        control={
                          <Controller
                            name='hasDeparted'
                            control={control}
                            render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                          />}
                      />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='oldStudentStatus' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="oldStudentStatus"
                            label={t('Old ?')} inputProps={{ readOnly: false }} focused
                          >
                            <MenuItem value={''}>::::</MenuItem>
                            <MenuItem value={'Y'}>{t('Yes')}</MenuItem>
                            <MenuItem value={'N'}>{t('No')}</MenuItem>
                          </TextField>
                        )}
                      />
                      <Controller name='repeaterStatus' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="repeaterStatus"
                            label={t('Repeater ?')} inputProps={{ readOnly: false }} focused >
                            <MenuItem value={''}>::::</MenuItem>
                            <MenuItem value={'Y'}>{t('Yes')}</MenuItem>
                            <MenuItem value={'N'}>{t('No')}</MenuItem>
                          </TextField>
                        )}
                      />
                      <Controller name='socialCaseStatus' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="socialCaseStatus"
                            label={t('Social case ?')} inputProps={{ readOnly: false }} focused >
                            <MenuItem value={''}>::::</MenuItem>
                            <MenuItem value={'Y'}>{t('Yes')}</MenuItem>
                            <MenuItem value={'N'}>{t('No')}</MenuItem>
                          </TextField>
                        )}
                      />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='amountToPayType' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="amountToPayType"
                            label={t('Type of amount to pay')} inputProps={{ readOnly: false }} focused
                          >
                            <MenuItem value={''}>::::</MenuItem>
                            <MenuItem value={'D'}>{t('Due amount')}</MenuItem>
                            <MenuItem value={'U'}>{t('Unpaid amount')}</MenuItem>
                          </TextField>
                        )}
                      />
                      <Controller
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                          <DatePicker label={t('Due date')}
                            onChange={onChange}
                            value={new Date(value)}
                            slotProps={{ textField: { sx: { width: 'calc(40% - 8px)' } } }}
                          //renderInput={(params) => <TextField {...params} sx={{width:`calc(40% - 8px)`}}  />}
                          />)}
                        name={'dueDate'}
                        control={control}
                      />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                        {`${t('The language use to send message')} ...`}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='languageSource' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                            label={t('Language')} inputProps={{ readOnly: false }} focused >
                            <MenuItem value='registration'>{`${t('The language of registration')}`}</MenuItem>
                            <MenuItem value='parent'>{`${t('The language of student')} ...`}</MenuItem>
                            <MenuItem value='parent'>{`${t('The language of parent')} ...`}</MenuItem>
                            <MenuItem value="fr-FR">{t('French')}</MenuItem>
                            <MenuItem value="en-US">{t('English')}</MenuItem>
                          </TextField>
                        )}
                      />

                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} key={`${watchCountRegistration}-${watchCountRecipient}-${watchCountDistinctRecipient}`}>
                      <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                        {`${watchCountRecipient}/${watchCountDistinctRecipient} ${t(('Recipient'))}(s), ${watchCountRegistration} ${t('Registration')}(s) / ${t('Student')}(s)`}
                      </Typography>
                    </Box>
                  </>}
                {(watchRecipientFilterOrigin === 'reportCard') &&
                  <>
                  </>}
              </>}
            </Stack>
          </Grid>

        </Grid>
      </Box>
    </FormProvider>
  )
}

